import {
  addPendingRequestIdForConversation,
  clearPendingRequest,
} from 'actions/composition/lib/update_conversation_workflow';
import ChatMessage from 'models/chat_message';
import ConversationMessageAttachment from 'models/conversation_message_attachment';
import createAgentChatMessage from 'scripts/domain/factories/conversation_item/create_agent_chat_message';
import CreateChatComposition from './create_chat_composition';
import ErrorReporter from 'scripts/infrastructure/error_reporter';
import Err from 'models/err';
import htmlToText from 'models/conversation_item/html_to_text';
import IdGenerator from 'scripts/domain/contracts/id_generator';
import { isEmpty } from 'models/composition/composition_content_shared';
import { ChatMessageType, OutgoingChatOperation } from 'models/chat_message_constants';
import SubmitComposition, { getOutgoingItemDto } from 'actions/composition/lib/submit_composition';
import unicodeByteLength from 'scripts/lib/unicode_byte_length';
import Upload from 'models/upload';

export default class SendChatMessage extends SubmitComposition {
  createOutgoingItems(composition, composedContent) {
    const isHtml = composedContent.messageType === ChatMessageType.HTML;
    const plainText = isHtml ? htmlToText(composedContent.bodyHtml || '') : composedContent.text;
    const bodyAttr = isHtml ? 'bodyHtml' : 'text';

    if (unicodeByteLength(plainText) > ChatMessage.MAX_MESSAGE_SIZE) {
      return {
        compositionErrors: [
          new Err({
            attr: bodyAttr,
            code: Err.Code.TOO_LONG,
            detail: `Your message can contain a maximum of ${ChatMessage.MAX_MESSAGE_SIZE} characters`,
          }),
        ],
      };
    }

    if (isEmpty(plainText) && !composition.hasAttachments()) {
      return {
        compositionErrors: [
          new Err({
            attr: bodyAttr,
            code: Err.Code.BLANK,
            detail: 'Message cannot be empty',
          }),
        ],
      };
    }

    let compositionErrors = composition.validateAttachmentStatus();
    compositionErrors = compositionErrors.concat(composition.validateTotalAttachmentSize());
    if (compositionErrors.length) {
      return { compositionErrors };
    }

    let attachments = this.createChatAttachments(composition);
    let chatMessageItems = [];
    const hasBody = !isEmpty(plainText);
    if (hasBody) {
      let chatMessageTextItem = createAgentChatMessage({
        agentProfile: this.currentAgent,
        content: toChatMessageContent(composedContent, isHtml),
        conversationId: composition.conversationId,
        customerId: composition.customerId,
        snippetIds: composition.snippetIds,
        relatedSnippetIds: composition.relatedSnippetIds,
        translation: composition.translation,
      });
      chatMessageItems.push(chatMessageTextItem);
    }
    for (let i = 0; i < attachments.length; i++) {
      composedContent.text = '';
      composedContent.bodyHtml = '';
      let attachment = attachments[i];
      let chatMessageAttachmentItem = createAgentChatMessage({
        agentProfile: this.currentAgent,
        content: {
          attachments: [attachment],
          ...composedContent,
        },
        conversationId: composition.conversationId,
        customerId: composition.customerId,
        snippetIds: composition.snippetIds,
        relatedSnippetIds: composition.relatedSnippetIds,
      });
      chatMessageItems.push(chatMessageAttachmentItem);
    }

    return { compositionErrors: [], conversationItems: chatMessageItems };
  }

  sendMessage(conversationUpdates, chatMessageItem) {
    let itemId = chatMessageItem.content.sessionItemId || chatMessageItem.content.sessionId;

    // remove conversationId and customerId from conversationItem payload because customer merge may cause a mismatch
    const payload = {
      outgoingCommunication: {
        id: chatMessageItem.id,
        customerId: this.currentCustomerId,
        conversation: conversationUpdates,
        conversationItem: getOutgoingItemDto(chatMessageItem),
      },
      operation: OutgoingChatOperation.SEND_CHAT_MESSAGE,
    };

    const requestId = IdGenerator.newId();
    addPendingRequestIdForConversation(this.context, chatMessageItem.conversationId, requestId);
    this.context.gateways.outgoingChatHttp
      .update({ agentId: this.currentAgent.id, itemId }, payload)
      .catch(err => {
        const reportedError = err instanceof Error ? err : new Error(String(err));
        ErrorReporter.reportError(reportedError, {
          extra: { context: 'Failed to send outgoing chat message', chatId: chatMessageItem.id },
        });
      })
      .finally(() => clearPendingRequest(this.context, requestId));
  }

  afterSend(content) {
    // create a new chat composition to continue chatting
    this.context.executeAction(CreateChatComposition, {
      conversationId: this.activeConversation.id,
      sessionId: content.sessionId,
    });
  }

  createChatAttachments(composition) {
    return composition.attachments.map(attachment => asChatAttachment(composition, attachment));
  }
}

function asChatAttachment(composition, attachment) {
  let fileDescriptor = attachment.fileDescriptor();
  let source = attachment instanceof Upload ? createUploadSource(composition, attachment.id) : attachment.source;

  return ConversationMessageAttachment.create({
    id: attachment.id,
    contentType: fileDescriptor.contentType,
    filename: fileDescriptor.filename,
    fileSize: fileDescriptor.contentLength,
    source,
  });
}

function createUploadSource(composition, attachmentId) {
  return {
    type: ConversationMessageAttachment.SourceType.UPLOAD,
    path: composition.getUploadPath(attachmentId),
  };
}

function toChatMessageContent(composedContent, isHtml) {
  if (!isHtml) {
    return composedContent;
  }
  const { bodyHtml, ...rest } = composedContent;
  return { ...rest, text: bodyHtml || '', messageType: ChatMessageType.HTML };
}
